import React, { memo, useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { FormControl } from 'react-bootstrap';
import { IoSearch } from 'react-icons/io5';
import { MESSAGES_PAGE_SIZE, MessagesFilterType, useGetAllMessagesQuery } from '../message.service';
import MessageItem from '../MessageItem';
import { Message } from '../message.types';
import routes from '../../../routes';
import { useAppDispatch, useAppSelector } from '../../../hooks/defaultHooks';
import { addChannel } from '../../../store/slices/messageChannelSlice';
import CustomBadge from '../../../UIComponents/CustomBadge';
import cls from './MessagesContainer.module.scss';
import { getUnreadCountChannel } from '../../../selectors/selectors';
import Spinner from '../../../UIComponents/Spinner';
import { useDebounce } from '../../../hooks/useDebounce';

const POLLING_INTERVAL = 15000;
/** How close to the bottom of the list the next window is requested, in px. */
const BOTTOM_THRESHOLD = 40;

const MessagesContainer = memo(() => {
  const dispatch = useAppDispatch();
  const unansweredCount = useAppSelector(getUnreadCountChannel);
  const [type, setType] = useState<MessagesFilterType>('UNREAD');
  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedSearchValue = useDebounce(searchValue, 500);
  // the window belongs to the tab and the search it was grown for, so switching
  // either of them falls back to the first window without an intermediate fetch
  const [loadedWindow, setLoadedWindow] = useState({
    type,
    search: '',
    size: MESSAGES_PAGE_SIZE,
  });
  const size =
    loadedWindow.type === type && loadedWindow.search === debouncedSearchValue
      ? loadedWindow.size
      : MESSAGES_PAGE_SIZE;

  const {
    data: dialogs = [],
    isLoading,
    isFetching,
  } = useGetAllMessagesQuery(
    { size, type, filterEmail: debouncedSearchValue },
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: POLLING_INTERVAL,
    },
  );
  const hasMoreDialogs = dialogs.length >= size;

  const scrollHandler = (event: React.UIEvent<HTMLDivElement>) => {
    const list = event.currentTarget;
    const distanceToBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
    if (distanceToBottom > BOTTOM_THRESHOLD || !hasMoreDialogs || isFetching) {
      return;
    }
    setLoadedWindow({
      type,
      search: debouncedSearchValue,
      size: size + MESSAGES_PAGE_SIZE,
    });
  };
  const clickHandler = (message: Message) => {
    dispatch(
      addChannel({
        id: message.consumerId,
        email: message.consumerEmail,
        name: message.consumerName,
      }),
    );
  };
  const changeTypeHandler = (nextType: MessagesFilterType) => {
    setType(nextType);
    setSearchValue('');
  };

  useEffect(() => {
    // the badge disappears together with the last unanswered dialog
    if (unansweredCount === 0 && type === 'UNFINALIZED') {
      setType('UNREAD');
    }
  }, [unansweredCount, type]);

  return (
    <div className={cls.Content} data-testid="users-messages">
      <div className={cls.Badges}>
        <CustomBadge
          handler={type === 'UNREAD' ? undefined : () => changeTypeHandler('UNREAD')}
          isActive={type === 'UNREAD'}
          text="All"
          className={cls.Badge}
        />
        {unansweredCount > 0 && (
          <CustomBadge
            handler={type === 'UNFINALIZED' ? undefined : () => changeTypeHandler('UNFINALIZED')}
            isActive={type === 'UNFINALIZED'}
            text={`Unanswered ${unansweredCount}`}
            className={cls.Badge}
          />
        )}
        <CustomBadge
          handler={type === 'ME' ? undefined : () => changeTypeHandler('ME')}
          isActive={type === 'ME'}
          text="Mine"
          className={cls.Badge}
        />
      </div>
      <div className="d-flex">
        <div className={cls.Aside} data-testid="dialogs-scroll" onScroll={scrollHandler}>
          <div className={cls.SearchField}>
            <IoSearch className={cls.SearchIcon} />
            <FormControl
              placeholder="Search"
              className={cls.Search}
              type="search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
          {isLoading && <Spinner />}
          {!isLoading && dialogs.length === 0 && <p className={cls.Empty}>No dialogs</p>}
          {dialogs.length > 0 && (
            <ul className={`messages-list ${cls.List}`} data-testid="message-list">
              {dialogs.map((message) => (
                <NavLink
                  to={routes.pages.messagePageByConsumerId(message.consumerId)}
                  key={message.consumerId}
                  data-testid="message-consumer"
                  onClick={() => clickHandler(message)}
                  className={({ isActive }) => (isActive ? `${cls.Item} ${cls.Active}` : cls.Item)}
                >
                  {<MessageItem message={message} />}
                </NavLink>
              ))}
            </ul>
          )}
        </div>
        <Outlet />
      </div>
    </div>
  );
});

export default MessagesContainer;
